import React, { useRef, useState } from 'react'
import cn from 'classnames'

import { Container } from '@components/Container'
import { Button } from '@components/Button'
import { Checkbox } from '@components/Checkbox'
import { CrossIcon, Upload2Icon } from '@components/Icons'
import { LoadingModal } from '@components/modals/LoadingModalV2/LoadingModal'
import { withStoryIdsModal } from './withStoryIdsModal'
import { WithStoryIdsModalProps, StoryIdTag } from './interface'

// StoryIdTag Component
function StoryIdTagComponent({
  tag,
  onRemove,
  onEdit,
  disabled,
}: {
  tag: StoryIdTag
  onRemove: (id: number) => void
  onEdit: (oldId: number, newId: number) => void
  disabled?: boolean
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(tag.id.toString())
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDoubleClick = () => {
    if (disabled) return
    setIsEditing(true)
    setEditValue(tag.id.toString())
    // Focus input after render
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleEditSubmit = () => {
    const newId = parseInt(editValue.trim(), 10)
    if (!Number.isNaN(newId) && newId > 0 && newId !== tag.id) {
      onEdit(tag.id, newId)
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleEditSubmit()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
      setEditValue(tag.id.toString())
    }
  }

  const handleBlur = () => {
    handleEditSubmit()
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type='text'
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className='w-[80px] px-2 py-1 text-sm border border-blue-3 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-3'
      />
    )
  }

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm cursor-pointer select-none',
        {
          'bg-gray-100 text-secondary':
            tag.isValid === null && !tag.isDuplicate,
          'bg-green-100 text-green-700 border border-green-300':
            tag.isValid === true && !tag.isDuplicate,
          'bg-amber-100 text-amber-700 border border-amber-300 line-through':
            tag.isDuplicate === true,
          'bg-red-100 text-red-700 border border-red-300 line-through':
            tag.isValid === false && !tag.isDuplicate,
        }
      )}
      onDoubleClick={handleDoubleClick}
      title='ดับเบิ้ลคลิกเพื่อแก้ไข'
    >
      <span>{tag.id}</span>
      {!disabled && (
        <button
          type='button'
          className='ml-1 hover:bg-gray-200 rounded-full p-0.5'
          onClick={e => {
            e.stopPropagation()
            onRemove(tag.id)
          }}
        >
          <CrossIcon width='12' height='12' />
        </button>
      )}
    </div>
  )
}

// Failed IDs Display Component
function FailedIdsDisplay({ failedIds }: { failedIds: number[] }) {
  if (failedIds.length === 0) return null

  return (
    <div className='mt-4 p-3 bg-red-50 border border-red-200 rounded-lg'>
      <p className='text-sm font-medium text-red-700 mb-2'>
        รายการที่ยิง API ไม่สำเร็จ ({failedIds.length} รายการ):
      </p>
      <div className='flex flex-wrap gap-2'>
        {failedIds.map(id => (
          <span
            key={id}
            className='px-2 py-1 bg-red-100 text-red-700 rounded text-sm'
          >
            {id}
          </span>
        ))}
      </div>
    </div>
  )
}

function StoryIdsModalComponent({
  mode,
  storyIds,
  isAutoSort,
  isValidating,
  isSubmitting,
  isValidated,
  canSubmit,
  isSubmitted,
  submitResult,
  addStoryIds,
  removeStoryId,
  updateStoryId,
  removeInvalidIds,
  removeDuplicateIds,
  clearAllIds,
  toggleAutoSort,
  handleValidate,
  handleSubmit,
  handleClose,
  parseCSVFile,
  hasDuplicateIds,
}: WithStoryIdsModalProps) {
  const [inputValue, setInputValue] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const hasInvalidIds = storyIds.some(t => t.isValid === false)

  const handleAddFromInput = () => {
    if (!inputValue.trim()) return
    const ids = inputValue
      .split(/[,\s\n]+/)
      .map(s => parseInt(s.trim(), 10))
      .filter(n => !Number.isNaN(n) && n > 0)
    if (ids.length > 0) {
      addStoryIds(ids)
      setInputValue('')
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault()
      handleAddFromInput()
    } else if (
      e.key === 'Backspace' &&
      inputValue === '' &&
      storyIds.length > 0
    ) {
      // Remove last tag when input is empty and backspace is pressed
      e.preventDefault()
      const lastTag = storyIds[storyIds.length - 1]
      removeStoryId(lastTag.id)
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const ids = pastedText
      .split(/[,\s\n]+/)
      .map(s => parseInt(s.trim(), 10))
      .filter(n => !Number.isNaN(n) && n > 0)
    if (ids.length > 0) {
      addStoryIds(ids)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const ids = await parseCSVFile(file)
      if (ids.length > 0) {
        addStoryIds(ids)
      }
    } catch {
      // CSV parsing error is handled silently
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <Container className='w-[600px] max-w-[90vw] pb-6'>
      <Container.Title className='text-center' showBackBtn={false}>
        {mode === 'create' ? 'เพิ่มนิยาย' : 'แก้ไขรายการนิยาย'}
      </Container.Title>

      {/* Success/Result Message */}
      {isSubmitted && submitResult && (
        <div
          className={cn('mb-4 p-4 rounded-lg', {
            'bg-green-50 border border-green-200': submitResult.success,
            'bg-yellow-50 border border-yellow-200': !submitResult.success,
          })}
        >
          <p
            className={cn('font-medium', {
              'text-green-700': submitResult.success,
              'text-yellow-700': !submitResult.success,
            })}
          >
            {submitResult.message}
          </p>
          {submitResult.failedIds.length > 0 && (
            <FailedIdsDisplay failedIds={submitResult.failedIds} />
          )}
        </div>
      )}

      {!isSubmitted && (
        <>
          {/* Combined Tag Input Box */}
          <div className='mb-4'>
            <div className='flex items-center justify-between mb-2'>
              <label className='text-sm font-medium text-secondary'>
                Story IDs ({storyIds.length} รายการ)
              </label>
              <div className='flex items-center gap-3'>
                {storyIds.length > 0 && (
                  <button
                    type='button'
                    className='text-xs text-red underline'
                    onClick={clearAllIds}
                  >
                    ลบทั้งหมด
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='.csv,.txt'
                  onChange={handleFileChange}
                  className='hidden'
                />
                <div className='flex items-center gap-1'>
                  <Button
                    type='button'
                    className='border border-gray text-secondary text-xs px-2 py-1'
                    icon={<Upload2Icon width='14' height='14' />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSubmitting}
                  >
                    Import CSV
                  </Button>
                  <a
                    href='data:text/csv;charset=utf-8,1234%0A5678%0A9012'
                    download='sample_story_ids.csv'
                    className='text-xs text-blue-3 underline ml-1'
                  >
                    ดาวน์โหลดตัวอย่าง
                  </a>
                </div>
              </div>
            </div>

            {/* CSV Format Info + Sort Checkbox */}
            <div className='flex items-center justify-between mb-2'>
              <p className='text-xs text-secondary-100'>
                รูปแบบ CSV: หนึ่ง ID ต่อบรรทัด หรือคั่นด้วย comma
              </p>
              <Checkbox
                label='เรียงลำดับ'
                checked={isAutoSort}
                onChange={toggleAutoSort}
              />
            </div>

            {/* Tag Input Box - Tags + Input รวมกัน */}
            <div
              className='min-h-[100px] max-h-[200px] overflow-y-auto border border-gray rounded-lg p-2 flex flex-wrap gap-2 items-start content-start cursor-text'
              onClick={() => inputRef.current?.focus()}
            >
              {storyIds.map(tag => (
                <StoryIdTagComponent
                  key={tag.id}
                  tag={tag}
                  onRemove={removeStoryId}
                  onEdit={updateStoryId}
                  disabled={isSubmitting}
                />
              ))}
              <input
                ref={inputRef}
                type='text'
                className='flex-grow min-w-[100px] border-none outline-none text-sm py-1 bg-transparent'
                placeholder={
                  storyIds.length === 0
                    ? 'พิมพ์ ID แล้วกด Enter หรือ comma เช่น 123, 456'
                    : 'เพิ่ม ID...'
                }
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onPaste={handlePaste}
                disabled={isSubmitting}
              />
            </div>

            <div className='flex items-center justify-between mt-2'>
              <p className='text-xs text-secondary-100'>
                กด Enter, Comma หรือ Spacebar เพื่อเพิ่ม • Backspace เพื่อลบ
              </p>
              <div className='flex gap-3'>
                {/* Remove duplicate IDs button */}
                {hasDuplicateIds && (
                  <button
                    type='button'
                    className='text-xs text-amber-600 underline'
                    onClick={removeDuplicateIds}
                  >
                    ลบ ID ที่มีอยู่แล้วทั้งหมด
                  </button>
                )}
                {/* Remove invalid IDs button */}
                {hasInvalidIds && (
                  <button
                    type='button'
                    className='text-xs text-red underline'
                    onClick={removeInvalidIds}
                  >
                    ลบ ID ที่ไม่พบทั้งหมด
                  </button>
                )}
              </div>
            </div>

            {/* UI Guide */}
            <div className='mt-3 p-2 bg-gray-50 rounded-lg border border-gray-200'>
              <p className='text-xs font-medium text-secondary mb-1.5'>
                คำอธิบายสี Tag:
              </p>
              <div className='flex flex-wrap gap-3 text-xs'>
                <span className='inline-flex items-center gap-1'>
                  <span className='w-3 h-3 rounded bg-gray-100 border border-gray-300' />
                  <span className='text-secondary-100'>รอตรวจสอบ</span>
                </span>
                <span className='inline-flex items-center gap-1'>
                  <span className='w-3 h-3 rounded bg-green-100 border border-green-300' />
                  <span className='text-secondary-100'>พร้อมเพิ่ม</span>
                </span>
                <span className='inline-flex items-center gap-1'>
                  <span className='w-3 h-3 rounded bg-amber-100 border border-amber-300' />
                  <span className='text-secondary-100'>มีอยู่แล้ว</span>
                </span>
                <span className='inline-flex items-center gap-1'>
                  <span className='w-3 h-3 rounded bg-red-100 border border-red-300' />
                  <span className='text-secondary-100'>ไม่พบ</span>
                </span>
              </div>
            </div>
          </div>

          {/* Validate Button */}
          <div className='mb-4 flex justify-center'>
            <Button
              type='button'
              className={cn(
                'px-6 text-sm',
                isValidated &&
                  !hasInvalidIds &&
                  !hasDuplicateIds &&
                  storyIds.length > 0
                  ? 'bg-green-500 text-white'
                  : 'bg-blue-3 text-white'
              )}
              onClick={() => {
                // Parse pending input and pass to handleValidate
                const pendingIds = inputValue
                  .split(/[,\s\n]+/)
                  .map(s => parseInt(s.trim(), 10))
                  .filter(n => !Number.isNaN(n) && n > 0)
                if (pendingIds.length > 0) {
                  setInputValue('')
                }
                handleValidate(pendingIds.length > 0 ? pendingIds : undefined)
              }}
              disabled={
                (storyIds.length === 0 && !inputValue.trim()) || isValidating
              }
            >
              {isValidating
                ? 'กำลังตรวจสอบ...'
                : isValidated &&
                  !hasInvalidIds &&
                  !hasDuplicateIds &&
                  storyIds.length > 0
                ? '✓ ตรวจสอบสำเร็จ'
                : 'ตรวจสอบ Story IDs'}
            </Button>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className='border-t border-gray pt-4 flex gap-4 justify-center'>
        <Button
          type='button'
          className='border border-secondary-100 text-secondary-100 min-w-[100px]'
          onClick={handleClose}
        >
          {isSubmitted ? 'ปิด' : 'ยกเลิก'}
        </Button>

        {!isSubmitted && (
          <Button
            type='button'
            className='bg-primary text-white min-w-[100px] disabled:bg-primary/50 disabled:cursor-not-allowed'
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'กำลังบันทึก...' : 'ยืนยัน'}
          </Button>
        )}
      </div>

      {/* Loading Overlay */}
      <LoadingModal isOpen={isValidating || isSubmitting} />
    </Container>
  )
}

export const StoryIdsModal = withStoryIdsModal(StoryIdsModalComponent)
